import Team from "@assets/icons/new/team.svg?react";
import { FaroTextField } from "@components/common/faro-text-field/faro-text-field";
import { PageInfoBar } from "@components/common/page-info-bar";
import { SphereAvatar } from "@components/header/sphere-avatar";
import { teamDisplayProperties } from "@components/table/teams/teams-table-utils";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { Box, Stack } from "@mui/material";
import { EditableTeamName } from "@pages/members/teams/team-details/editable-team-name";
import { TeamDetailsInfoBarAction } from "@pages/members/teams/team-details/team-details-info-bar-action";
import { useAppSelector } from "@store/store-helper";
import { selectedTeamWithRoleSelector } from "@store/teams/teams-selector";
import { formatUserRoleType } from "@utils/data-display";

interface Props {
  /** Flag whether the content should be shown as skeletons because it is still loading */
  isLoading?: boolean;
}

/**
 * Info bar for the team overview tabs
 */
export function TeamDetailsInfoBar({ isLoading = false }: Props): JSX.Element {
  const selectedTeamId = useAppSelector(selectedTeamWithRoleSelector);
  const { canEditTeams } = useHasUserValidRoleCompanyLevel();

  if (!selectedTeamId || isLoading) {
    // Early exit with a loading skeleton if the team is not yet loaded
    return <PageInfoBar isLoading={true} />;
  }

  return (
    <PageInfoBar
      items={[
        {
          columnSize: 2,
          content: (
            <Stack flexDirection={"row"} gap={"12px"} alignItems={"center"}>
              <SphereAvatar
                icon={<Team style={{ height: "26px", width: "26px" }} />}
                size="medium"
                backgroundColor={teamDisplayProperties[selectedTeamId.category.kind].color}
                shouldHideWhiteRim={true}
              />
              <Box
                component="div"
                width="100%"
              >
                <EditableTeamName
                  team={selectedTeamId}
                  isEditable={canEditTeams}
                />
              </Box>
            </Stack>
          ),
        },
        {
          columnSize: 4,
          content: (
            <FaroTextField
              label="Workspace role"
              initialValue={formatUserRoleType(selectedTeamId.role)}
              faroVariant="main"
              fontSize="18px"
            />
          ),
        },
      ]}
      action={<TeamDetailsInfoBarAction selectedTeam={selectedTeamId} />}
    />
  );
}
